import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { fyStart } from '@/features/reports/settings';
import { useBudgets, type Budget, type BudgetSummary } from '@/lib/accountant';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { useCompanySettings } from '@/lib/settings';

export const Route = createFileRoute('/accounting/budgets/')({ component: Budgets });

const addYears = (iso: string, n: number) => `${Number(iso.slice(0, 4)) + n}${iso.slice(4)}`;
const yearLabel = (start: string) =>
  start.slice(5, 7) === '01'
    ? `FY ${start.slice(0, 4)}`
    : `FY ${start.slice(0, 4)}/${String((Number(start.slice(0, 4)) + 1) % 100).padStart(2, '0')}`;

/** Screen A11 (document 06): budgets, list pattern. */
function Budgets() {
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const budgets = useBudgets();
  const isStaff = me.data?.isStaff ?? false;
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<BudgetSummary | null>(null);
  const remove = useMutation({
    mutationFn: (b: BudgetSummary) =>
      api<void>(`/orgs/${orgId}/budgets/${b.id}`, { method: 'DELETE' }),
    onSuccess: async (_v, b) => {
      await qc.invalidateQueries({ queryKey: ['budgets', orgId] });
      setDeleting(null);
      toast(`Budget "${b.name}" deleted.`);
    },
  });
  if (!orgId) return <EmptyState title="Select a client" />;
  const runReport = (b: BudgetSummary) =>
    void navigate({
      to: '/reports/$reportKey',
      params: { reportKey: 'budget-vs-actuals' },
      search: { budget: b.id, from: b.fiscalYearStart, to: lastDay(b.fiscalYearStart) },
    });
  return (
    <div>
      <PageHeader
        title="Budgets"
        description="Plan income and expenses by account and month, then compare them with the actuals."
        actions={isStaff ? <Button onClick={() => setCreating(true)}>New budget</Button> : null}
      />
      {budgets.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : budgets.isError ? (
        <EmptyState title="Could not load budgets" hint={budgets.error.message} />
      ) : !budgets.data.length ? (
        <EmptyState
          title="No budgets yet"
          hint="Start from last year's actuals with an uplift, or from a blank year."
          action={
            isStaff ? <Button onClick={() => setCreating(true)}>New budget</Button> : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Financial year</TableHead>
                <TableHead className="text-right">Income</TableHead>
                <TableHead className="text-right">Expenses</TableHead>
                <TableHead className="text-right">Net income</TableHead>
                <TableHead>Last modified</TableHead>
                <TableHead className="w-[180px]">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {budgets.data.map((b) => (
                <TableRow key={b.id} data-testid={`budget-${b.name}`}>
                  <TableCell>
                    <Link
                      to="/accounting/budgets/$budgetId"
                      params={{ budgetId: b.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {b.name}
                    </Link>
                  </TableCell>
                  <TableCell>{b.fiscalYearLabel}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(b.total.income)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(b.total.expense)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(b.total.net)}</TableCell>
                  <TableCell>
                    {formatDateTime(b.updatedAt)}
                    {b.updatedBy ? (
                      <span className="text-muted-foreground"> · {b.updatedBy}</span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Button
                        variant="link"
                        size="sm"
                        className="px-1"
                        onClick={() => runReport(b)}
                      >
                        Run report
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`More actions for ${b.name}`}
                          >
                            <ChevronDown className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() =>
                              void navigate({
                                to: '/accounting/budgets/$budgetId',
                                params: { budgetId: b.id },
                              })
                            }
                          >
                            {isStaff ? 'Edit' : 'View'}
                          </DropdownMenuItem>
                          {isStaff ? (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() => setDeleting(b)}
                              >
                                Delete
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <NewBudgetDialog
        open={creating}
        budgets={budgets.data ?? []}
        onClose={() => setCreating(false)}
        onCreated={(b) =>
          void navigate({ to: '/accounting/budgets/$budgetId', params: { budgetId: b.id } })
        }
      />
      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete budget “{deleting?.name}”?</DialogTitle>
            <DialogDescription>
              Its amounts are removed. The books and the actuals are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Last day of the twelve months starting on `start`. */
function lastDay(start: string): string {
  const y = Number(start.slice(0, 4));
  const m = Number(start.slice(5, 7));
  return new Date(Date.UTC(y + 1, m - 1, 0)).toISOString().slice(0, 10);
}

function NewBudgetDialog({
  open,
  budgets,
  onClose,
  onCreated,
}: {
  open: boolean;
  budgets: BudgetSummary[];
  onClose: () => void;
  onCreated: (b: Budget) => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const company = useCompanySettings();
  const current = fyStart(todayIso(), company.data?.advanced.fiscalYearStart ?? 1);
  const years = [addYears(current, -1), current, addYears(current, 1)];
  const [name, setName] = useState('');
  const [year, setYear] = useState(current);
  const [from, setFrom] = useState<'blank' | 'actuals' | 'budget'>('actuals');
  const [sourceYear, setSourceYear] = useState(addYears(current, -1));
  const [sourceBudget, setSourceBudget] = useState('');
  const [uplift, setUplift] = useState('0');
  const create = useMutation({
    mutationFn: () =>
      api<Budget>(`/orgs/${orgId}/budgets`, {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim() || `Budget ${yearLabel(year)}`,
          fiscalYearStart: year,
          prefill:
            from === 'blank'
              ? { from }
              : from === 'actuals'
                ? { from, yearStart: sourceYear, upliftPct: Number(uplift) || 0 }
                : { from, budgetId: sourceBudget, upliftPct: Number(uplift) || 0 },
        }),
      }),
    onSuccess: async (b) => {
      await qc.invalidateQueries({ queryKey: ['budgets', orgId] });
      toast(`Budget "${b.name}" created.`);
      onClose();
      onCreated(b);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          create.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New budget</DialogTitle>
          <DialogDescription>
            A budget covers one financial year, by profit and loss account and month.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <ErrorBanner>{create.error?.message ?? null}</ErrorBanner>
          <Field label="Name">
            {(id) => (
              <Input
                id={id}
                value={name}
                placeholder={`Budget ${yearLabel(year)}`}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
              />
            )}
          </Field>
          <Field label="Financial year">
            {(id) => (
              <NativeSelect id={id} value={year} onChange={(e) => setYear(e.target.value)}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {yearLabel(y)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Start from">
            {(id) => (
              <NativeSelect
                id={id}
                value={from}
                onChange={(e) => setFrom(e.target.value as 'blank' | 'actuals' | 'budget')}
              >
                <option value="actuals">Actuals of a financial year</option>
                <option value="budget" disabled={!budgets.length}>
                  Another budget
                </option>
                <option value="blank">Blank</option>
              </NativeSelect>
            )}
          </Field>
          {from === 'actuals' ? (
            <Field label="Actuals of">
              {(id) => (
                <NativeSelect
                  id={id}
                  value={sourceYear}
                  onChange={(e) => setSourceYear(e.target.value)}
                >
                  {[addYears(current, -2), ...years.slice(0, 2)].map((y) => (
                    <option key={y} value={y}>
                      {yearLabel(y)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          {from === 'budget' ? (
            <Field label="Budget">
              {(id) => (
                <NativeSelect
                  id={id}
                  value={sourceBudget}
                  onChange={(e) => setSourceBudget(e.target.value)}
                >
                  <option value="">Choose a budget</option>
                  {budgets.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          {from !== 'blank' ? (
            <Field label="Change by (%)" hint="10 adds 10% to every amount; −5 takes 5% off.">
              {(id) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  className="tnum w-32 text-right"
                  value={uplift}
                  onChange={(e) => setUplift(e.target.value.replace(/[^0-9.-]/g, ''))}
                />
              )}
            </Field>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={create.isPending || (from === 'budget' && !sourceBudget)}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create budget'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
